<template>
    <Modal class="num-pad-modal"
           size="sm"
           :icon="icon"
           :title="title"
           :custom-title-text="customTitleText">
        <DialogText :text-lang-keys="textLangKeys"
                    :custom-text="customText"
                    :custom-html="customHtml "
                    class="text-center" />

        <form class="form-v">
            <NumPad v-bind="(numPadOptions)"
                    v-model="form.value"
                    @ok="ok" />

            <DialogCustomInputs v-if="customInputs"
                                :custom-inputs="customInputs"
                                :form="form"
                                @enter="ok" />
        </form>

        <template v-slot:footer>
            <div class="modl-footer confirm-modal__btns">
                <DialogCancelBtn :color="cancelColor"
                                 :custom-text="cancelCustomText"
                                 :label="cancelLabel"
                                 @click="cancel" />
                <DialogOkBtn :color="okColor"
                             :custom-text="okCustomText"
                             :label="okLabel"
                             @click="ok" />
            </div>
        </template>
    </Modal>
</template>

<script>
import DialogCancelBtn from '@/vue/modals/Dialogs/components/DialogCancelBtn';
import DialogCustomInputs from '@/vue/modals/Dialogs/components/DialogCustomInputs';
import DialogOkBtn from '@/vue/modals/Dialogs/components/DialogOkBtn';
import DialogText from '@/vue/modals/Dialogs/components/DialogText';
import Modal from '@/vue/components/Modals/Modal';
import NumPad from '@/vue/components/NumPad/NumPad';
import { container } from '@/container';
import { currencyFilter } from '@/vue/filters/currencyFilter';
import { DialogPropsMixin } from '@/vue/modals/Dialogs/mixins/DialogPropsMixin';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    components: {
        Modal,
        DialogCustomInputs,
        NumPad,
        DialogOkBtn,
        DialogCancelBtn,
        DialogText,
    },

    mixins: [
        ModalPropsMixin,
        DialogPropsMixin,
    ],

    props: {
        icon: {
            type: String,
            default: 'fa-input-numeric',
        },

        /**
         * Object of props to pass to the child <NumPad> component.
         */
        numPadOptions: {
            type: Object,
            default: () => ({}),
        },

        initialValue: {
            type: Number,
            required: true,
        },

        max: {
            type: Number,
            default: null,
        },

        min: {
            type: Number,
            default: null,
        },

        // Custom inputs - see the DialogCustomInputs component.
        customInputs: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            form: {
                value: this.initialValue,
            },
        };
    },

    computed: {
        hasMin() {
            return this.min !== null;
        },

        hasMax() {
            return this.max !== null;
        },
    },

    methods: {
        ok() {
            let value = this.form.value;

            const valueIsBelowMin = this.hasMin && value < this.min;
            const valueIsAboveMax = this.hasMax && value > this.max;

            if (valueIsBelowMin || valueIsAboveMax) {
                // The value is not within the valid range. Display an error.
                // The text displayed depends on if a min, max, or both where specified.
                let textKey;

                if (this.hasMin && this.hasMax) {
                    textKey = 'DIALOGS.NUM_PAD_OUT_OF_RANGE';
                } else if (this.hasMin) {
                    // This must be a below min error because there is no max.
                    textKey = 'DIALOGS.NUM_PAD_BELOW_MIN';
                } else if (this.hasMax) {
                    // This must be an above max error because there is no min.
                    textKey = 'DIALOGS.NUM_PAD_ABOVE_MAX';
                }

                let displayMin = null;
                if (this.hasMin) {
                    displayMin = this.showValueAsCurrency ? currencyFilter(this.min) : this.min;
                }

                let displayMax = null;
                if (this.hasMax) {
                    displayMax = this.showValueAsCurrency ? currencyFilter(this.max) : this.max;
                }

                container.toastFactory.warningTop(
                    null,
                    {
                        key: textKey,
                        params: {
                            min: displayMin,
                            max: displayMax,
                        },
                    }
                );

                return;
            }

            this.resolve(this.form);
        },
    },
};
</script>
